import {
  CheckBoxOutlineBlank,
  CheckBoxRounded,
  Edit,
  ExpandMore,
  History,
  Save
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { FC, useState } from 'react';
import { Button, Table } from '../../../components';
import { formatDate } from '../../../helpers';
import { ISalesToolsContract, ISalesToolsContractTemplate } from '../../../models';
import { useQuery } from 'react-query';
import {
  getSalesToolsContractTemplates,
  updateDocumentFromPandaDocTemplate,
  updateSalesToolsContractTemplate
} from '../../../fetch';
import { ContractManagementHistoryModal } from './ContractManagementHistoryModal';
import { useSnackbar } from 'notistack';
import { ContractMangementUploadButton } from './ContractMangementUploadButton';

interface IContractManagementAccordionProps {
  contract: ISalesToolsContract;
}

export const ContractManagementAccordion: FC<IContractManagementAccordionProps> = ({
  contract
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isContractHistoryModalOpen, setIsContractHistoryModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: templates,
    isLoading,
    refetch: fetchSalesToolsContractTemplates,
    isFetching
  } = useQuery(
    ['getSalesToolsContractTemplates-contract-management', contract.buildDocumentTypeId],
    () => getSalesToolsContractTemplates(contract.buildDocumentTypeId),
    {
      enabled: !!contract.buildDocumentTypeId && isExpanded
    }
  );
  return (
    <>
      <Accordion
        key={contract.buildDocumentTypeId}
        TransitionProps={{ unmountOnExit: true }}
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            '&& .MuiAccordionSummary-content': {
              margin: 0
            }
          }}
        >
          <Box display="flex" alignItems="center" width="100%">
            <Box flex={1}>
              <Typography variant="h6">{contract.name}</Typography>
            </Box>
            <Box display="flex" alignItems="center" mr={1}>
              <ContractMangementUploadButton
                buildDocumentTypeId={contract.buildDocumentTypeId}
                fetchSalesToolsContractTemplates={() => fetchSalesToolsContractTemplates()}
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: `0px 32px 8px`
          }}
        >
          <Divider sx={{ marginBottom: '0.5rem' }} />
          <Table
            columns={[
              {
                Header: 'Active',
                accessor: 'isActive',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: ISalesToolsContractTemplate } };
                }) => {
                  if (templates.length > 1) {
                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        {original?.isActive ? <CheckBoxRounded color="primary" /> : ''}
                        {!original?.isActive && (
                          <Tooltip
                            title={
                              original.isDeletedInPandaDoc
                                ? 'Template is deleted in Pandadoc'
                                : 'Set as "Active"'
                            }
                          >
                            <span>
                              <IconButton
                                sx={{ padding: 0 }}
                                size="small"
                                disabled={original.isDeletedInPandaDoc}
                                onClick={async () => {
                                  try {
                                    setIsUpdating(true);
                                    await updateSalesToolsContractTemplate(
                                      contract.buildDocumentTypeId,
                                      {
                                        activePandaDocTemplateId: original.pandaDocTemplateId
                                      }
                                    );
                                    fetchSalesToolsContractTemplates();
                                    enqueueSnackbar(`File updated!`, {
                                      variant: 'success'
                                    });
                                  } catch (error: any) {
                                    enqueueSnackbar(error?.Detail ?? `Error updating file.`, {
                                      variant: 'error'
                                    });
                                  } finally {
                                    setIsUpdating(false);
                                  }
                                }}
                                color="primary"
                              >
                                <CheckBoxOutlineBlank />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  }
                  return <>{original?.isActive ? <CheckBoxRounded color="primary" /> : ''}</>;
                }
              },
              {
                Header: 'Created Date',
                accessor: 'createdDate',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: ISalesToolsContractTemplate } };
                }) => {
                  const formattedDate = formatDate(original?.createdDate);
                  return <div>{formattedDate}</div>;
                }
              },
              {
                Header: 'File Name',
                accessor: 'documentName',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: ISalesToolsContractTemplate } };
                }) => {
                  return <Typography>{original?.documentName}</Typography>;
                }
              },
              {
                Header: 'Updated Date',
                accessor: 'updatedDate',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: ISalesToolsContractTemplate } };
                }) => {
                  const formattedDate = formatDate(original?.updatedDate);
                  return <div>{formattedDate}</div>;
                }
              },

              {
                Header: '',
                accessor: 'actions',
                sort: false,
                Cell: ({
                  cell: {
                    row: { original }
                  }
                }: {
                  cell: { row: { original: ISalesToolsContractTemplate } };
                }) => {
                  return (
                    <Box display="flex" gap={1}>
                      <Tooltip
                        title={
                          original.isDeletedInPandaDoc ? 'Template is deleted in Pandadoc' : ''
                        }
                      >
                        <span>
                          <Button
                            id="edit"
                            startIcon={<Edit />}
                            disabled={original.isDeletedInPandaDoc}
                            variant="text"
                            color="primary"
                            asLink
                            to={`/sales/sales-tools/contract-management/${contract.buildDocumentTypeId}/${original.pandaDocTemplateId}`}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      {original.isActive && (
                        <Button
                          id="update"
                          startIcon={<Save />}
                          onClick={async () => {
                            try {
                              setIsUpdating(true);
                              await updateDocumentFromPandaDocTemplate(
                                contract.buildDocumentTypeId,
                                original.pandaDocTemplateId
                              );
                              enqueueSnackbar('Template updated successfully', {
                                variant: 'success'
                              });
                              fetchSalesToolsContractTemplates();
                            } catch (error: any) {
                              enqueueSnackbar(error?.Detail ?? 'Error updating template', {
                                variant: 'error'
                              });
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          variant="text"
                          color="primary"
                          disabled={isUpdating || original.isDeletedInPandaDoc}
                        >
                          Save Template
                        </Button>
                      )}
                    </Box>
                  );
                }
              }
            ]}
            data={templates ?? []}
            isLoading={isLoading || isFetching || isUpdating}
            noResultsText="No results"
            hidePagination
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              id="contract-history"
              startIcon={<History />}
              onClick={() => setIsContractHistoryModalOpen(true)}
              variant="text"
              color="primary"
            >
              History
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
      {isContractHistoryModalOpen && (
        <ContractManagementHistoryModal
          handleClose={() => setIsContractHistoryModalOpen(false)}
          buildDocumentTypeId={contract.buildDocumentTypeId}
          fetchSalesToolsContractTemplates={fetchSalesToolsContractTemplates}
        />
      )}
    </>
  );
};
